import React from 'react';
import styled, { keyframes } from 'styled-components';
import { QuestionCircleOutlined } from '@ant-design/icons';

interface SuggestedQuestion {
    question: string;
    description: string;
}

interface SuggestedQuestionsProps {
    questions: SuggestedQuestion[];
    onQuestionClick: (question: string) => void;
    isLoading?: boolean;
}

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const Container = styled.div`
    margin: 8px 0;
    padding: 8px 12px;
    border-radius: 8px;
    background-color: rgba(0, 0, 0, 0.02);
    animation: ${fadeIn} 0.3s ease;
`;

const Title = styled.div`
    font-size: 12px;
    color: #666;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const QuestionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const QuestionItem = styled.div`
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    background-color: #fff;
    border: 1px solid #e8e8e8;
    transition: all 0.3s;

    &:hover {
        background-color: #f5f5f5;
        border-color: #d9d9d9;
    }
`;

const Question = styled.div`
    font-size: 14px;
    color: #1890ff;
    margin-bottom: 4px;
`;

const Description = styled.div`
    font-size: 12px;
    color: #999;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2px;
    color: #666;
    gap: 2px;
    min-height: 16px;
`;

const LoadingDots = styled.span`
    display: inline-block;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: #1890ff;
    opacity: 0.6;
    animation: ${fadeIn} 0.6s infinite alternate;
    margin: 0 1px;

    &:nth-child(2) {
        animation-delay: 0.2s;
    }

    &:nth-child(3) {
        animation-delay: 0.4s;
    }
`;

const SuggestedQuestions: React.FC<SuggestedQuestionsProps> = ({ 
    questions, 
    onQuestionClick,
    isLoading 
}) => {
    if (isLoading) {
        return (
            <Container>
                <LoadingContainer>
                    <LoadingDots />
                    <LoadingDots />
                    <LoadingDots />
                </LoadingContainer>
            </Container>
        );
    }

    return (
        <Container>
            <Title>
                <QuestionCircleOutlined />
                <span>您可能想问</span>
            </Title>
            <QuestionList>
                {questions.map((q, index) => (
                    <QuestionItem key={index} onClick={() => onQuestionClick(q.question)}>
                        <Question>{q.question}</Question>
                        <Description>{q.description}</Description>
                    </QuestionItem>
                ))}
            </QuestionList>
        </Container>
    );
};

export default SuggestedQuestions; 